import { Link } from "react-router-dom";
import { AlertTriangle, Quote, Star } from "lucide-react";
import { CtaExt, Eyebrow } from "./Bits";
import { Reveal } from "./Motion";
import { SAMPLE_REVIEWS } from "../data/content";

export const SafetyStrip = ({ compact = false }) => (
  <aside aria-labelledby="safety-h" className="bg-flame-wash text-ink" data-testid="safety-strip">
    <div className={`mx-auto flex max-w-7xl flex-col gap-4 px-5 sm:px-8 md:flex-row md:items-center ${compact ? "py-5" : "py-7"}`}>
      <AlertTriangle aria-hidden="true" className="h-7 w-7 shrink-0 text-flame-deep" />
      <div className="flex-1">
        <h2 id="safety-h" className="font-display text-xl uppercase tracking-tight">Smell gas or hear a CO alarm?</h2>
        <p className="text-sm text-slate2">Leave the home first. From outside, call 911 or your gas utility's emergency line. Don't use switches or phones inside. An HVAC visit comes after the home is cleared.</p>
      </div>
      <Link to="/services/carbon-monoxide-gas-safety" className="link-u min-h-[44px] content-center text-sm font-semibold text-flame-deep" data-testid="safety-strip-link">Gas and CO safety guidance</Link>
    </div>
  </aside>
);

export const SampleReviews = () => (
  <section aria-labelledby="reviews-h" className="bg-bone py-24" data-testid="sample-reviews">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow>Sample review layout</Eyebrow>
      <h2 id="reviews-h" className="h2-display mt-3 max-w-3xl">How verified customer reviews would appear</h2>
      <p className="mt-4 max-w-2xl text-slate2">These are illustrative examples, not real customer reviews. On a live client site, only genuine, verifiable reviews appear here.</p>
      <ul className="mt-12 grid gap-6 md:grid-cols-3">
        {SAMPLE_REVIEWS.map((r, i) => (
          <li key={r.who}>
            <Reveal delay={i * 0.08} className="card-bone h-full">
              <div className="flex items-center justify-between">
                <span className="flex gap-0.5 text-flame-deep" role="img" aria-label="Sample five star rating">{[...Array(5)].map((_, k) => <Star key={k} aria-hidden="true" className="h-4 w-4 fill-current" />)}</span>
                <span className="rounded-full bg-ink px-2.5 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-bone">Sample</span>
              </div>
              <Quote aria-hidden="true" className="mt-6 h-6 w-6 text-flame-deep" />
              <p className="mt-3 text-lg leading-relaxed text-ink">{r.text}</p>
              <p className="mt-6 text-sm text-slate2">{r.who} (illustrative)</p>
            </Reveal>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export const CtaBand = ({ title = "This could be your company's website.", body = "Denver Furnace Repair is a VisibilityReady™ demonstration. See how the same architecture, buyer guidance, and machine-readable structure can represent an established contractor." }) => (
  <section aria-labelledby="cta-h" className="grain relative overflow-hidden bg-fog py-28 text-ink" data-testid="cta-band">
    <div className="spot pointer-events-none absolute inset-0" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Eyebrow light>For established contractors</Eyebrow>
      <h2 id="cta-h" className="mt-4 max-w-4xl font-display text-5xl uppercase leading-[0.95] tracking-tight sm:text-6xl">{title}</h2>
      <p className="mt-6 max-w-2xl text-mist">{body}</p>
      <div className="mt-10 flex flex-wrap gap-4">
        <CtaExt testid="cta-band-external">Explore VisibilityReady™ demonstrations</CtaExt>
        <Link to="/about-this-demonstration" className="btn-outline-light" data-testid="cta-band-about">About this demonstration</Link>
      </div>
    </div>
  </section>
);

export const FitList = ({ title, items, tone = "good" }) => (
  <div className={`card-bone h-full ${tone === "good" ? "" : "bg-bone2"}`}>
    <h3 className="font-display text-2xl uppercase tracking-tight">{title}</h3>
    <ul className="mt-5 space-y-3">
      {items.map((t) => (
        <li key={typeof t === "string" ? t : t.text} className="flex gap-3 text-slate2">
          <span aria-hidden="true" className={`mt-2 h-1.5 w-1.5 shrink-0 rotate-45 ${tone === "good" ? "bg-flame-deep" : "bg-ink"}`} />
          {typeof t === "string" ? <span>{t}</span> : <span>{t.text} <Link to={t.to} className="link-u font-semibold text-ink">Go to the related page</Link></span>}
        </li>
      ))}
    </ul>
  </div>
);
